import { ValidationErrorData } from './common';
import { RetargetingConditionId } from './retargeting';

export type RateCorrectionConditionId = string;

export enum RateCorrectionType {
    retargeting = 'retargeting',
    inventory = 'inventory',
    demography = 'demography',
    device = 'device',
    videoAddition = 'videoAddition',
    smartBanners = 'smartBanners',
    weather = 'weather',
    trafficJam = 'trafficJam'
}

export enum ExtraRateCorrectionType {
    abSegments = 'abSegments'
}

export enum RateControlMode {
    up = 'up',
    down = 'down',
    none = 'none'
}

export interface RateControlValue {
    mode: RateControlMode;
    value?: number;
}

export enum RateCorrectionGender {
    male = 'male',
    female = 'female',
    all = 'all'
}

export enum RateCorrectionAge {
    all = 'all',
    age0_17 = '0-17',
    age18_24 = '18-24',
    age25_34 = '25-34',
    age35_44 = '35-44',
    age45_54 = '45-54',
    age55_ = '55-'
}

export enum DeviceRateCorrectionType {
    allMobile = 'all_mobile',
    ios = 'ios',
    android = 'android',
    desktops = 'desktop'
}

export enum InventoryRateCorrectionType {
    inbanner = 'inbanner',
    inpage = 'inpage',
    instreamWeb = 'instreamWeb',
    interstitial = 'interstitial',
    rewarded = 'rewarded',
    cpmBanner = 'cpmBanner'
}

export const INVENTORY_VIDEO_CORRECTIONS = [
    InventoryRateCorrectionType.inbanner,
    InventoryRateCorrectionType.interstitial,
    InventoryRateCorrectionType.instreamWeb,
    InventoryRateCorrectionType.rewarded,
    InventoryRateCorrectionType.inpage
];

export const INVENTORY_AD_TYPE_CORRECTIONS = [InventoryRateCorrectionType.cpmBanner];

export enum WeatherRateCorrectionsType {
    temperature = 'temperature',
    rainfall = 'rainfall',
    cloudiness = 'cloudiness'
}

export enum TrafficJamRateCorrectionsType {
    free = 'free',
    light = 'light',
    hard = 'hard',
    veryHard = 'very_hard'
}

export interface CommonRateCorrectionCondition {
    id: RateCorrectionConditionId;
    value: number;
    serverId?: RateCorrectionConditionId;
}

export interface InventoryRateCorrectionCondition extends CommonRateCorrectionCondition {
    inventoryType: InventoryRateCorrectionType;
}

export interface DemographyRateCorrectionCondition extends CommonRateCorrectionCondition {
    age: RateCorrectionAge;
    gender: RateCorrectionGender;
}

export interface RetargetingRateCorrectionCondition extends CommonRateCorrectionCondition {
    retargetingConditionId: RetargetingConditionId;
}

export interface DeviceRateCorrectionCondition extends CommonRateCorrectionCondition {
    deviceType: DeviceRateCorrectionType;
}

export interface ABSegmentsRateCorrectionCondition extends CommonRateCorrectionCondition {
    sectionId: string;
    segmentId: string;
}

export type VideoAdditionRateCorrectionCondition = CommonRateCorrectionCondition;
export type SmartBannersRateCorrectionCondition = CommonRateCorrectionCondition;

export interface WeatherRateCorrectionConditionCloudiness {
    id: RateCorrectionConditionId;
    serverId?: RateCorrectionConditionId;
    value?: number;
    weatherType: WeatherRateCorrectionsType.cloudiness;
    cloudinessIds: Array<CloudinessTypes>;
}

export interface WeatherRateCorrectionConditionRainfall {
    id: RateCorrectionConditionId;
    serverId?: RateCorrectionConditionId;
    value?: number;
    weatherType: WeatherRateCorrectionsType.rainfall;
    rainfallIds: Array<RainfallTypes>;
}

export interface WeatherRateCorrectionConditionTemperature {
    id: RateCorrectionConditionId;
    serverId?: RateCorrectionConditionId;
    value?: number;
    weatherType: WeatherRateCorrectionsType.temperature;
    minTemperature: number;
    maxTemperature: number;
}

export type WeatherRateCorrectionCondition =
    | WeatherRateCorrectionConditionCloudiness
    | WeatherRateCorrectionConditionRainfall
    | WeatherRateCorrectionConditionTemperature;

export interface TrafficJamRateCorrectionCondition {
    id: RateCorrectionConditionId;
    serverId?: RateCorrectionConditionId;
    value?: number;
    trafficJamType: TrafficJamRateCorrectionsType;
}

export type RateCorrectionCondition =
    | DemographyRateCorrectionCondition
    | DeviceRateCorrectionCondition
    | RetargetingRateCorrectionCondition
    | VideoAdditionRateCorrectionCondition
    | SmartBannersRateCorrectionCondition
    | WeatherRateCorrectionCondition
    | TrafficJamRateCorrectionCondition;

export interface BaseRateCorrection {
    isSwitchedOn?: boolean;
    mode?: RateCorrectionMode;
    singleMassRateControlValue?: number;
}

export interface InventoryRateCorrection extends BaseRateCorrection {
    type: RateCorrectionType.inventory;
    items: Array<InventoryRateCorrectionCondition>;
}

export interface DeviceRateCorrection extends BaseRateCorrection {
    type: RateCorrectionType.device;
    items: Array<DeviceRateCorrectionCondition>;
}

export interface RetargetingRateCorrection extends BaseRateCorrection {
    type: RateCorrectionType.retargeting;
    items: Array<RetargetingRateCorrectionCondition>;
}

export interface VideoAdditionRateCorrection extends BaseRateCorrection {
    type: RateCorrectionType.videoAddition;
    items: Array<VideoAdditionRateCorrectionCondition>;
}

export interface DemographyRateCorrection extends BaseRateCorrection {
    type: RateCorrectionType.demography;
    items: Array<DemographyRateCorrectionCondition>;
}

export interface SmartBannersRateCorrection extends BaseRateCorrection {
    type: RateCorrectionType.smartBanners;
    items: Array<SmartBannersRateCorrectionCondition>;
}

export interface WeatherRateCorrection extends BaseRateCorrection {
    type: RateCorrectionType.weather;
    items: Array<WeatherRateCorrectionCondition>;
}

export interface ABSegmentsRateCorrection extends BaseRateCorrection {
    type: ExtraRateCorrectionType.abSegments;
    items: Array<ABSegmentsRateCorrectionCondition>;
}

export interface TrafficJamRateCorrection extends BaseRateCorrection {
    type: RateCorrectionType.trafficJam;
    items: Array<TrafficJamRateCorrectionCondition>;
}

export type RateCorrection =
    | DeviceRateCorrection
    | InventoryRateCorrection
    | RetargetingRateCorrection
    | DemographyRateCorrection
    | VideoAdditionRateCorrection
    | SmartBannersRateCorrection
    | WeatherRateCorrection
    | TrafficJamRateCorrection;

export interface EditorCommonRateCorrectionCondition {
    id: RateCorrectionConditionId;
    serverId?: RateCorrectionConditionId;
    value: RateControlValue;
    errors: Record<string, ValidationErrorData[]>;
}

export interface EditorInventoryRateCorrectionCondition extends EditorCommonRateCorrectionCondition {
    inventoryType: InventoryRateCorrectionType;
}

export interface EditorDemographyRateCorrectionCondition extends EditorCommonRateCorrectionCondition {
    age?: RateCorrectionAge;
    gender?: RateCorrectionGender;
}

export interface EditorRetargetingRateCorrectionCondition extends EditorCommonRateCorrectionCondition {
    retargetingConditionId: RetargetingConditionId;
}

export interface EditorDeviceRateCorrectionCondition extends EditorCommonRateCorrectionCondition {
    deviceType: DeviceRateCorrectionType;
}

export type EditorVideoAdditionRateCorrectionCondition = EditorCommonRateCorrectionCondition;
export type EditorSmartBannersRateCorrectionCondition = EditorCommonRateCorrectionCondition;

export interface EditorWeatherRateCorrectionConditionCloudiness extends EditorCommonRateCorrectionCondition {
    weatherType: WeatherRateCorrectionsType.cloudiness;
    cloudinessIds: Array<CloudinessTypes>;
}

export interface EditorWeatherRateCorrectionConditionRainfall extends EditorCommonRateCorrectionCondition {
    weatherType: WeatherRateCorrectionsType.rainfall;
    rainfallIds: Array<RainfallTypes>;
}

export interface EditorWeatherRateCorrectionConditionTemperature extends EditorCommonRateCorrectionCondition {
    weatherType: WeatherRateCorrectionsType.temperature;
    minTemperature: number;
    maxTemperature: number;
}

export interface EditorTrafficJamRateCorrectionCondition extends EditorCommonRateCorrectionCondition {
    trafficJamType: TrafficJamRateCorrectionsType;
}

export type EditorWeatherRateCorrectionCondition =
    | EditorWeatherRateCorrectionConditionCloudiness
    | EditorWeatherRateCorrectionConditionRainfall
    | EditorWeatherRateCorrectionConditionTemperature;

export type EditorRateCorrectionCondition =
    | EditorDemographyRateCorrectionCondition
    | EditorDeviceRateCorrectionCondition
    | EditorInventoryRateCorrectionCondition
    | EditorRetargetingRateCorrectionCondition
    | EditorVideoAdditionRateCorrectionCondition
    | EditorSmartBannersRateCorrectionCondition
    | EditorWeatherRateCorrectionCondition
    | EditorTrafficJamRateCorrectionCondition;

export interface EditorBaseRateCorrection {
    isSwitchedOn?: boolean;
    massRateControlValue?: RateControlValue;
    maxConditionsNumber?: number;
    maxRate?: number;
    minRate?: number;
    hasChanges: boolean;
    errors: ValidationErrorData[];
    mode?: RateCorrectionMode;
    singleMassRateControlValue?: RateControlValue;
    errorsSingleMassRateControl?: ValidationErrorData[];
    minTemperatureLimit?: number;
    maxTemperatureLimit?: number;
}

export interface EditorInventoryRateCorrection extends EditorBaseRateCorrection {
    type: RateCorrectionType.inventory;
    items: Array<EditorInventoryRateCorrectionCondition>;
}

export interface EditorDeviceRateCorrection extends EditorBaseRateCorrection {
    type: RateCorrectionType.device;
    items: Array<EditorDeviceRateCorrectionCondition>;
}

export interface EditorRetargetingRateCorrection extends EditorBaseRateCorrection {
    type: RateCorrectionType.retargeting;
    items: Array<EditorRetargetingRateCorrectionCondition>;
}

export interface EditorVideoAdditionRateCorrection extends EditorBaseRateCorrection {
    type: RateCorrectionType.videoAddition;
    items: Array<EditorVideoAdditionRateCorrectionCondition>;
}

export interface EditorDemographyRateCorrection extends EditorBaseRateCorrection {
    type: RateCorrectionType.demography;
    items: Array<EditorDemographyRateCorrectionCondition>;
}

export interface EditorSmartBannersRateCorrection extends EditorBaseRateCorrection {
    type: RateCorrectionType.smartBanners;
    items: Array<EditorSmartBannersRateCorrectionCondition>;
}

export interface EditorWeatherRateCorrection extends EditorBaseRateCorrection {
    type: RateCorrectionType.weather;
    items: Array<EditorWeatherRateCorrectionCondition>;
}

export interface EditorTrafficJamRateCorrection extends EditorBaseRateCorrection {
    type: RateCorrectionType.trafficJam;
    items: Array<EditorTrafficJamRateCorrectionCondition>;
}

export type EditorRateCorrection =
    | EditorInventoryRateCorrection
    | EditorDeviceRateCorrection
    | EditorRetargetingRateCorrection
    | EditorDemographyRateCorrection
    | EditorVideoAdditionRateCorrection
    | EditorSmartBannersRateCorrection
    | EditorWeatherRateCorrection
    | EditorTrafficJamRateCorrection;

export interface RateCorrectionEditorState {
    isVisible: boolean;
    currentTabType?: RateCorrectionType;
    value: RatesCorrectionsEditorValue;
}

export interface RateCorrectionConditionChooserItem {
    id: string;
    name: string;
    hint?: string;
}

export interface RateCorrectionConditionChooserItemsGroup {
    name?: string;
    hasMultiChoice: boolean;
    items: Array<RateCorrectionConditionChooserItem>;
}

export type RatesCorrectionsValue = Record<RateCorrectionType, RateCorrection>;
export type RatesCorrectionsEditorValue = Record<RateCorrectionType, EditorRateCorrection>;

export interface BaseRatesCorrectionsConstants {
    maxConditions: number;
    percentRange: {
        max: number;
        min: number;
        step: number;
    };
}

export interface RatesCorrectionsMobileConstants extends BaseRatesCorrectionsConstants {
    type: RateCorrectionType.device;
    osTypes: DeviceRateCorrectionType[];
}

export interface RatesCorrectionsInventoryConstants extends BaseRatesCorrectionsConstants {
    type: RateCorrectionType.inventory;
    inventoryTypes: InventoryRateCorrectionType[];
}

export interface RatesCorrectionsRetargetingConstants extends BaseRatesCorrectionsConstants {
    type: RateCorrectionType.retargeting;
}

export interface RatesCorrectionsWeatherConstants extends BaseRatesCorrectionsConstants {
    type: RateCorrectionType.weather;
}

export interface RatesCorrectionsVideoConstants extends BaseRatesCorrectionsConstants {
    type: RateCorrectionType.videoAddition;
}

export interface RatesCorrectionsSmartBannersConstants extends BaseRatesCorrectionsConstants {
    type: RateCorrectionType.smartBanners;
}

export interface RatesCorrectionsDemographyConstants extends BaseRatesCorrectionsConstants {
    type: RateCorrectionType.demography;
}

export interface RatesCorrectionsABSegmentsConstants extends BaseRatesCorrectionsConstants {
    type: ExtraRateCorrectionType.abSegments;
}

export interface RatesCorrectionsTrafficConstants extends BaseRatesCorrectionsConstants {
    type: RateCorrectionType.trafficJam;
}

export type RatesCorrectionsConstantsData = {
    [RateCorrectionType.device]?: RatesCorrectionsMobileConstants;
    [RateCorrectionType.retargeting]?: RatesCorrectionsRetargetingConstants;
    [RateCorrectionType.weather]?: RatesCorrectionsWeatherConstants;
    [RateCorrectionType.videoAddition]?: RatesCorrectionsVideoConstants;
    [RateCorrectionType.smartBanners]?: RatesCorrectionsSmartBannersConstants;
    [RateCorrectionType.demography]?: RatesCorrectionsDemographyConstants;
    [ExtraRateCorrectionType.abSegments]?: RatesCorrectionsABSegmentsConstants;
    [RateCorrectionType.trafficJam]?: RatesCorrectionsTrafficConstants;
    [RateCorrectionType.inventory]?: RatesCorrectionsInventoryConstants;
};

export enum CloudinessTypes {
    clear = 'clear',
    partly = 'partly',
    medium = 'medium',
    significant = 'significant',
    overcast = 'overcast'
}

export enum RainfallTypes {
    none = 'none',
    light = 'light',
    average = 'average',
    heavy = 'heavy',
    rainSnow = 'rainSnow'
}

export enum RateCorrectionMode {
    allDone = 'allDone',
    oneDone = 'oneDone'
}

export interface RetargetingConditionDataForRateCorrections {
    id: RetargetingConditionId;
    name: string;
}
